import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { allowedWebsiteTemplatesApi } from '@/lib/pillarApi';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import type {
    CreateAllowedWebsiteTemplateRequest,
    RbsWebsite,
} from '@anchorbase/pillar-sdk';
import {
    AllowedWebsiteTemplateEditor,
    AllowedWebsiteTemplateValidationPanel,
    DefaultAllowedWebsiteTemplateEditorState,
    buildAllowedWebsiteTemplateCreateRequest,
    buildAllowedWebsiteTemplateEditorStateFromTransfer,
    getAllowedWebsiteTemplateValidationIssues,
    type AllowedWebsiteTemplateEditorState,
    type AllowedWebsiteTemplateValidationIssue,
} from '../AllowedWebsiteTemplateEditor';
import { AllowedWebsiteTemplateImportButton } from '../AllowedWebsiteTemplateTransferControls';

export default function CreateAllowedWebsiteTemplatePage() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [editorState, setEditorState] =
        useState<AllowedWebsiteTemplateEditorState>(
            DefaultAllowedWebsiteTemplateEditorState
        );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [importedRbsWebsite, setImportedRbsWebsite] = useState<
        RbsWebsite | null | undefined
    >(undefined);

    const validationIssues: ReadonlyArray<AllowedWebsiteTemplateValidationIssue> =
        useMemo(
            () => getAllowedWebsiteTemplateValidationIssues(editorState),
            [editorState]
        );

    const createMutation = useMutation({
        mutationFn: async (request: CreateAllowedWebsiteTemplateRequest) =>
            allowedWebsiteTemplatesApi.createTemplate(request),
        onSuccess: (response) => {
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url:
                        '/allowed-website-templates/' +
                        encodeURIComponent(
                            response.template.allowed_website_template_uuid
                        ),
                    title: 'Allowed Website Template Details',
                },
            });
        },
        onError: (error) => {
            setErrorMessage(error instanceof Error ? error.message : 'Create failed');
        },
    });

    const canSubmit: boolean =
        validationIssues.length === 0 && createMutation.isPending === false;

    function handleSubmit(): void {
        setErrorMessage(null);
        let request: CreateAllowedWebsiteTemplateRequest;
        try {
            request = buildAllowedWebsiteTemplateCreateRequest(editorState);
            if (importedRbsWebsite !== undefined) {
                request = {
                    ...request,
                    rbs_website: importedRbsWebsite,
                };
            }
        } catch (error) {
            setErrorMessage(
                error instanceof Error ? error.message : 'Validation failed'
            );
            return;
        }
        createMutation.mutate(request);
    }

    return (
        <div className="mx-auto max-w-4xl p-6">
            <div className="mb-4 flex items-center justify-between gap-4">
                <h1 className="text-2xl font-bold">
                    Create Allowed Website Template
                </h1>
                <div className="flex gap-3">
                    <AllowedWebsiteTemplateImportButton
                        onImport={(transfer) => {
                            setEditorState(
                                buildAllowedWebsiteTemplateEditorStateFromTransfer(
                                    transfer
                                )
                            );
                            setImportedRbsWebsite(transfer.rbs_website);
                            setErrorMessage(null);
                        }}
                        onError={setErrorMessage}
                    />
                    <Button
                        variant="outline"
                        onClick={() => {
                            breadcrumbNavigate({
                                source: 'panel',
                                target: {
                                    url: '/allowed-website-templates',
                                    title: 'Allowed Website Templates',
                                },
                            });
                        }}
                    >
                        Back
                    </Button>
                </div>
            </div>

            <div className="space-y-4">
                {errorMessage !== null ? (
                    <Card className="border-red-300">
                        <CardHeader>
                            <CardTitle className="text-base text-red-700">
                                Error
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm">{errorMessage}</p>
                        </CardContent>
                    </Card>
                ) : null}

                <AllowedWebsiteTemplateValidationPanel issues={validationIssues} />

                {importedRbsWebsite !== undefined ? (
                    <Card>
                        <CardContent className="p-4 text-sm">
                            Imported template file loaded.{' '}
                            {importedRbsWebsite === null
                                ? 'It contains no RBS workflows.'
                                : 'Its complete RBS workflow configuration will be created.'}
                        </CardContent>
                    </Card>
                ) : null}

                <AllowedWebsiteTemplateEditor
                    state={editorState}
                    onChange={setEditorState}
                />

                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={() => {
                            breadcrumbNavigate({
                                source: 'panel',
                                target: {
                                    url: '/allowed-website-templates',
                                    title: 'Allowed Website Templates',
                                },
                            });
                        }}
                    >
                        Cancel
                    </Button>
                    <Button disabled={!canSubmit} onClick={handleSubmit}>
                        {createMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
